
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import BrandsGrid from '@/components/Brands/BrandsGrid';

const BrandsPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Top Roofing Material Brands | Carolina Trophy Roofs | Quality Materials</title>
        <meta name="description" content="Carolina Trophy Roofs partners with premium roofing manufacturers: GAF, Owens Corning, CertainTeed, and more. Quality materials with comprehensive warranties for Eastern NC homes and businesses." />
        <meta name="keywords" content="roofing brands, GAF, Owens Corning, CertainTeed, roofing materials, shingles, metal roofing, Eastern NC, quality materials" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/brands" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Top Roofing Material Brands | Carolina Trophy Roofs" />
        <meta property="og:description" content="Premium roofing materials from trusted manufacturers including GAF, Owens Corning, and CertainTeed." />
        <meta property="og:url" content="https://www.carolinatrophyroofs.com/brands" />
        
        {/* Schema.org Product Catalog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "name": "Roofing Material Brands",
            "description": "Premium roofing materials from top manufacturers",
            "provider": {
              "@type": "RoofingContractor",
              "name": "Carolina Trophy Roofs"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/80 text-white py-20 md:py-28 overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/5f3827e5-0c99-43d2-80dc-04b1857cd807.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold drop-shadow-lg">
              Premium Roofing Brands We Trust
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-md">
              Carolina Trophy Roofs partners with industry-leading manufacturers to provide you with the highest quality roofing materials backed by reliable warranties.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8">
                  Get a Free Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Our Partners</Badge>
            <h2 className="text-3xl font-bold mb-4">Trusted Brand Partnerships</h2>
            <p className="text-gray-600">
              We've carefully selected these premium roofing brands to ensure we provide our customers with products that meet our high standards for quality, durability, and value.
            </p>
          </div>

          <BrandsGrid />
        </div>
      </section>

      {/* Why We Choose These Brands */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4">Quality Assurance</Badge>
              <h2 className="text-3xl font-bold mb-4">Why We Choose These Brands</h2>
              <p className="text-gray-600">
                Our selection process ensures that we only work with manufacturers who share our commitment to excellence.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="bg-brand-safety-orange/10 p-3 rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-brand-safety-orange">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Superior Durability</h3>
                  <p className="text-gray-600">
                    All our partner brands are rigorously tested to withstand North Carolina's diverse weather conditions, from intense summer heat to occasional severe storms. We only offer products that have demonstrated exceptional longevity and performance.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="bg-brand-safety-orange/10 p-3 rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-brand-safety-orange">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Comprehensive Warranties</h3>
                  <p className="text-gray-600">
                    Our selected manufacturers back their products with substantial warranties, often ranging from 25 years to lifetime coverage. As certified installers for these brands, we can offer enhanced warranty options that protect both materials and workmanship.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="bg-brand-safety-orange/10 p-3 rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-brand-safety-orange">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Aesthetic Versatility</h3>
                  <p className="text-gray-600">
                    We understand that your roof is a significant design element of your home or building. Our brand partners offer diverse style options, colors, and profiles to complement any architectural style while providing the protection you need.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="bg-brand-safety-orange/10 p-3 rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-brand-safety-orange">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Innovation & Sustainability</h3>
                  <p className="text-gray-600">
                    We prioritize brands that invest in research and development to create more energy-efficient, environmentally responsible, and technologically advanced roofing solutions. Many of our offerings include cool roof technologies, recycled materials, and other sustainable features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-16 bg-brand-charcoal-gray text-white overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/05712f55-6608-4d58-b71e-fc4dd31ec82a.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 text-center z-10">
          <h2 className="text-3xl font-bold mb-6">Interested in Learning More?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us to discuss which roofing material and brand would be best suited for your specific needs and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold">
                Request a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BrandsPage;
